import { useEffect, useState } from "react";
import classNames from "classnames";
import { SpeechToTextOverlayProps } from "duck/ui/chat/hooks/useSpeechToText";
import { useTranslation } from "react-i18next";
import ArrowCircleUpOutlinedIcon from "@mui/icons-material/ArrowCircleUpOutlined";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";

import IconButton from "features/components/IconButton";
import Typography from "features/components/Typography";

import AudioWaveform from "./AudioWaveform";
import LoadingSpinner from "./LoadingSpinner";

const BORDER_SHRINK_DELAY_MS = 400;

const SpeechToTextOverlay = ({
  isPressToSubmit,
  isConnecting,
  isRecording,
  isWaiting,
  analyserNode,
  hasText,
  onCancel,
  onSubmit,
}: SpeechToTextOverlayProps) => {
  const { t } = useTranslation("duck");
  const [borderWidthPx, setBorderWidthPx] = useState(2);

  useEffect(() => {
    setBorderWidthPx(2);
    const timer = setTimeout(() => setBorderWidthPx(1), BORDER_SHRINK_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isWaiting, isRecording]);

  const cancelTooltip = isPressToSubmit
    ? t("cancel_recording")
    : t("exit_conversation_mode");

  return (
    <Box className="flex items-center gap-2 p-2">
      <IconButton
        onClick={onCancel}
        color="secondary"
        size="small"
        data-testid="duck-cancel-recording-cta"
        tooltip={cancelTooltip}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <Box
        className={classNames(
          "flex flex-1 items-center h-7.5 justify-center gap-2 rounded-sm px-1 py-0.5 transition-all duration-300 ease-out border-solid",
          {
            "bg-red-100 border-red-400": isRecording,
            "bg-gray-100 border-0": !isRecording,
            "border-2": borderWidthPx === 2 && !isWaiting,
            border: borderWidthPx === 1 && !isWaiting,
          }
        )}
      >
        {isConnecting ? (
          <>
            <LoadingSpinner />
            <Typography variant="body2">{t("connecting")}</Typography>
          </>
        ) : (
          <AudioWaveform
            analyserNode={isRecording ? analyserNode : null}
            colorClass={isWaiting ? "bg-gray-400" : "bg-red-400"}
          />
        )}
      </Box>
      {isPressToSubmit ? (
        <IconButton
          onClick={onSubmit}
          color="primary"
          size="small"
          disabled={!hasText}
          data-testid="duck-stt-submit-cta"
          tooltip={t("send")}
        >
          <ArrowCircleUpOutlinedIcon />
        </IconButton>
      ) : null}
    </Box>
  );
};

export default SpeechToTextOverlay;
